import React, { useState, useEffect } from "react";
import axiosInstance from "../utils/axios";
import { API_ENDPOINTS } from "../config/api";

const NewEquipmentModal = ({ isOpen, onClose, initialData = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    node: "",
    category: "Power Supply",
    assetNo: "",
    tagNo: "",
    brand: "",
    model: "",
    acquisitionYear: "",
  });

  // Investment Inventory Category mapping
  const categoryOptions = [
    { value: "Other", label: "Diğer" },
    { value: "Power Supply", label: "Güç Kaynağı" },
    { value: "Oscilloscope", label: "Osiloskop" },
    { value: "Network Analyzer", label: "Ağ Analizörü" },
    { value: "Signal Analyzer", label: "Sinyal Analizörü" },
    { value: "Spectrum Analyzer", label: "Spektrum Analizörü" },
    { value: "Power Amplifier", label: "Güç Yükseltici" },
    { value: "IFF Test Device", label: "IFF Test Cihazı" },
  ];

  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [nodesLoaded, setNodesLoaded] = useState(false);

  // Set initial data when in edit mode
  useEffect(() => {
    if (isEditMode && initialData && isOpen) {
      setFormData({
        node: initialData.deviceId || "",
        category: initialData.investmentInventoryCategory || "Power Supply",
        assetNo: initialData.fixedAssetNumber || "",
        tagNo: initialData.tagNumber || "",
        brand: initialData.brand || "",
        model: initialData.model || "",
        acquisitionYear: initialData.yearOfInventory || "",
      });
    }
  }, [isEditMode, initialData, isOpen]);

  // Fetch nodes from API only once when modal opens for the first time (only for create mode)
  useEffect(() => {
    const fetchNodes = async () => {
      if (nodesLoaded || isEditMode) return; // Don't fetch if already loaded or in edit mode

      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.NODES_WITHOUT_METADATA);

        const data = response.data;
        setNodes(data);
        setNodesLoaded(true);
        // Set first node as default if available
        if (data.length > 0) {
          setFormData((prev) => ({
            ...prev,
            node: data[0].deviceId || "",
          }));
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "API isteği başarısız";
        setError(errorMessage);
        console.error("Error fetching nodes:", err);
        if (err.response) {
          console.error("Response data:", err.response.data);
          console.error("Response status:", err.response.status);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && !nodesLoaded && !isEditMode) {
      fetchNodes();
    }
  }, [isOpen, nodesLoaded, isEditMode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Prepare data for backend
      const requestData = {
        metadata: {
          brand: formData.brand,
          model: formData.model,
          investmentInventoryCategory: formData.category,
          fixedAssetNumber: formData.assetNo,
          tagNumber: formData.tagNo,
          yearOfInventory: formData.acquisitionYear,
        },
      };

      console.log("Sending data:", requestData);
      console.log("Node ID:", formData.node);

      let response;
      if (isEditMode) {
        // Update existing node
        response = await axiosInstance.put(`${API_ENDPOINTS.UPDATE_NODE_WITH_METADATA}/${formData.node}`, requestData, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        console.log("Node updated successfully:", response.data);
      } else {
        // Create new node
        response = await axiosInstance.post(`${API_ENDPOINTS.ADD_METADATA_TO_NODE}/${formData.node}`, requestData.metadata, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        console.log("Metadata added successfully:", response.data);
      }

      // Show success message
      setSuccessMessage(isEditMode ? "Node metadata'sı başarıyla güncellendi!" : "Node metadata'sı başarıyla eklendi!");

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

      // Reset form only for create mode
      if (!isEditMode) {
        setFormData({
          node: "",
          category: "Power Supply",
          assetNo: "",
          tagNo: "",
          brand: "",
          model: "",
          acquisitionYear: "",
        });
      }

      // Close modal and reset states after a short delay
      setTimeout(() => {
        onClose();
        if (!isEditMode) {
          setNodesLoaded(false); // Reset to allow fresh data on next open
        }
      }, 1000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || (isEditMode ? "Node metadata'sı güncellenirken hata oluştu" : "Node metadata'sı eklenirken hata oluştu");
      setError(errorMessage);
      console.error("Error:", err);
      if (err.response) {
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Success Message - Top Right */}
      {successMessage && (
        <div
          className="fixed z-[9999] w-80 h-16 p-4 bg-white text-gray-800 rounded-lg shadow-lg relative overflow-hidden animate-slide-in-right"
          style={{
            position: "fixed",
            top: "16px",
            right: "16px",
            left: "auto",
            transform: "none",
          }}
        >
          <div className="flex items-center h-full">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
          {/* Animated progress bar */}
          <div className="absolute bottom-0 left-0 h-1 bg-green-400 animate-progress"></div>
        </div>
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4" style={{ maxWidth: "736px" }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{isEditMode ? "Tracker Düzenle" : "Yeni Tracker Tanımla"}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" style={{ backgroundColor: "##1570EF" }}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">{error}</div>}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Node */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Node</label>
                  <select
                    name="node"
                    value={formData.node}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${loading || isEditMode ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed" : "border-gray-300"
                      }`}
                    disabled={loading || isEditMode}
                  >
                    {loading ? (
                      <option value="">Yükleniyor...</option>
                    ) : error ? (
                      <option value="">Hata: {error}</option>
                    ) : isEditMode ? (
                      <option value={formData.node}>{formData.node}</option>
                    ) : nodes.length === 0 ? (
                      <option value="">Node bulunamadı</option>
                    ) : (
                      <>
                        <option value="">Node seçiniz</option>
                        {nodes.map((node, index) => (
                          <option key={index} value={node.deviceId}>
                            {node.deviceId}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Yatırım Envanter Kategorisi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yatırım Envanter Kategorisi</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Duran Varlık No */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duran Varlık No</label>
                  <input
                    type="text"
                    name="assetNo"
                    value={formData.assetNo}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                  />
                </div>

                {/* Etiket No */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etiket No</label>
                  <input
                    type="text"
                    name="tagNo"
                    value={formData.tagNo}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Marka */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marka</label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                  />
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                  />
                </div>

                {/* Envantere Alım Yılı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Envantere Alım Yılı</label>
                  <input
                    type="text"
                    name="acquisitionYear"
                    value={formData.acquisitionYear}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                  />
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end space-x-3 mt-8">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                Vazgeç
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#1570EF" }}
              >
                {submitting ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default NewEquipmentModal;
